import { useState } from 'react'
import type { CSSProperties, RefObject } from 'react'
import { Button as AriaButton, Dialog, Popover } from 'react-aria-components'
import { Button, SwatchPicker, TextField } from '@bloo-team/ui'
import { useCreateSignalTag } from '../../../lib/signals'
import { tagInk } from '../../../lib/signal-tags'

/**
 * Signal tags in the Signal block: the tag badge, and the "New signal
 * tag" popover the tag picker's create row opens (signals/tag/create).
 */

/**
 * Tag color swatches. Design tokens, not hex (rule 3): a token is resolved to
 * its hex only when the tag is sent, since tag_color is stored as hex.
 */
export const TAG_SWATCHES = [
  { id: 'none', label: 'None', token: null },
  { id: 'mint', label: 'Mint', token: '--bloo-signal' },
  { id: 'azure', label: 'Azure', token: '--bloo-signal-2' },
  { id: 'deep-azure', label: 'Deep azure', token: '--bloo-signal-2-dk' },
  { id: 'amber', label: 'Amber', token: '--bloo-amber' },
  { id: 'deep-amber', label: 'Deep amber', token: '--bloo-amber-dk' },
  { id: 'red', label: 'Red', token: '--bloo-error' },
  { id: 'saffron', label: 'Saffron', token: '--bloo-saffron' },
] as const

/** Allowed tag names, checked after trimming. */
const TAG_NAME = /^[A-Za-z0-9 ._-]+$/
const TAG_NAME_HINT = 'Use letters, digits, spaces, dots, - and _ only.'

const resolveToken = (token: string) => getComputedStyle(document.documentElement).getPropertyValue(token).trim()

/** A `var(--token)` colour as the hex it resolves to; any other colour as given. */
const resolveColor = (color: string) => {
  const token = /^var\((--[\w-]+)\)$/.exec(color.trim())
  return token ? resolveToken(token[1]!) : color
}

/**
 * A tag as a badge — the STORYLINE badge's shape, filled with the tag's own
 * colour and inked dark or light for contrast (a tag without a readable
 * colour takes the accent). ✕ when it can be removed.
 */
export function TagChip({ value, color, onRemove }: { value: string; color?: string; onRemove?: () => void }) {
  const ink = color ? tagInk(resolveColor(color)) : null
  return (
    <span className="sg-badge tag-badge" data-ink={ink ?? undefined} style={ink ? ({ '--tag-fill': color } as CSSProperties) : undefined}>
      <span>{value}</span>
      {onRemove ? (
        <AriaButton className="tag-badge-x" aria-label={`Remove tag ${value}`} onPress={onRemove}>
          <span aria-hidden="true">✕</span>
        </AriaButton>
      ) : null}
    </span>
  )
}

/** The swatch picker's options: each token as a CSS var, None as no color. */
const SWATCH_OPTIONS = TAG_SWATCHES.map((s) => ({ id: s.id, label: s.label, color: s.token ? `var(${s.token})` : null }))

/**
 * The create form, anchored under the tag picker. Mounted only while open,
 * so it starts from `initialName` each time. Create calls the backend at once
 * (tags are scope-wide, not the workbook's); success closes it, a refusal
 * keeps it open with the server's message. The new tag is not selected.
 */
export function SignalTagCreate({
  triggerRef,
  initialName,
  clusterId,
  scopeId,
  onClose,
}: {
  triggerRef: RefObject<Element | null>
  initialName: string
  clusterId: string
  scopeId: string
  onClose: () => void
}) {
  const create = useCreateSignalTag({ clusterId, scopeId })
  const [name, setName] = useState(initialName)
  const [swatch, setSwatch] = useState<string>('none')
  const value = name.trim()
  const invalid = value !== '' && !TAG_NAME.test(value)
  const token = TAG_SWATCHES.find((s) => s.id === swatch)?.token ?? null

  const submit = () => {
    if (!value || invalid || create.isPending) return
    create.mutate({ value, color: token ? resolveToken(token) : '' }, { onSuccess: onClose })
  }

  return (
    <Popover
      className="pick-pop"
      triggerRef={triggerRef}
      placement="bottom start"
      isOpen
      onOpenChange={(open) => {
        if (!open && !create.isPending) onClose()
      }}
    >
      <Dialog className="pick-dialog wb-tagnew" aria-label="New signal tag">
        <form
          className="hud-stack wb-gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            submit()
          }}
        >
          <span className="wb-tagnew-title">New signal tag</span>
          <div className="wb-tagnew-row">
            <span className="wb-tagnew-k">preview</span>
            <span data-testid="tag-preview">
              <TagChip value={value || 'tag name'} color={token ? `var(${token})` : ''} />
            </span>
          </div>
          <TextField
            label="Tag name"
            value={name}
            onChange={(v) => {
              setName(v)
              create.reset()
            }}
            error={invalid ? TAG_NAME_HINT : undefined}
            // The popover opens from the create row to edit this name; focus belongs in it.
            // eslint-disable-next-line jsx-a11y/no-autofocus -- see above
            autoFocus
          />
          <div className="wb-tagnew-row">
            <span className="wb-tagnew-k">color</span>
            <SwatchPicker label="Tag color" swatches={SWATCH_OPTIONS} value={swatch} onChange={setSwatch} />
          </div>
          {create.error ? (
            <div className="bui-alert bui-alert-error" role="alert">
              <span className="bui-alert-mark">!</span>
              <span>{create.error.message}</span>
            </div>
          ) : null}
          <div className="wb-tagnew-actions">
            <Button variant="ghost" size="sm" isDisabled={create.isPending} onPress={onClose}>
              Cancel
            </Button>
            <Button size="sm" type="submit" isDisabled={!value || invalid || create.isPending}>
              {create.isPending ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </form>
      </Dialog>
    </Popover>
  )
}
